import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { Tables } from '@/types/database'

export function useChecklist(taskId: string | null) {
  return useQuery({
    queryKey: ['checklist', taskId ?? 'none'],
    enabled: !!taskId,
    queryFn: async (): Promise<Tables<'task_checklist_items'>[]> => {
      const { data, error } = await supabase
        .from('task_checklist_items')
        .select('*')
        .eq('task_id', taskId!)
        .order('position', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useChecklistMutations(taskId: string) {
  const { user } = useAuth()
  const { workspaceId } = useWorkspace()
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['checklist', taskId] })
    void qc.invalidateQueries({ queryKey: qk.tasks(workspaceId ?? 'none') })
  }

  const add = useMutation({
    mutationFn: async (label: string) => {
      const { count } = await supabase
        .from('task_checklist_items')
        .select('id', { count: 'exact', head: true })
        .eq('task_id', taskId)
      const { error } = await supabase
        .from('task_checklist_items')
        .insert({ task_id: taskId, label, position: ((count ?? 0) + 1) * 1024 })
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const toggle = useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase
        .from('task_checklist_items')
        .update({
          is_done: done,
          done_by: done ? (user?.id ?? null) : null,
          done_at: done ? new Date().toISOString() : null,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('task_checklist_items').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { add, toggle, remove }
}
